import React, { Suspense, useState } from 'react';
import styled from 'styled-components';
import { useQuillEditor } from './useQuillEditor';
import { FormulaRequest, RichTextEditorProps } from './types';

// Lazy so MathLive (~700KB) is split into its own chunk and loads only
// when someone actually opens the equation editor.
const MathModal = React.lazy(() => import('./MathModal'));

/**
 * A failure while loading/mounting the equation dialog must not white-
 * screen the whole authoring page: degrade to closing the dialog and
 * reporting through the app snackbar.
 */
class FormulaModalBoundary extends React.Component<
	{ onFail: () => void },
	{ failed: boolean }
> {
	state = { failed: false };

	static getDerivedStateFromError() {
		return { failed: true };
	}

	componentDidCatch() {
		this.props.onFail();
	}

	render() {
		return this.state.failed ? null : this.props.children;
	}
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
	value,
	onChange,
	onEditorChange,
	min_height,
	max_height,
	disabled,
	preset,
	uploadImage,
	onError,
}) => {
	const [formulaRequest, setFormulaRequest] = useState<FormulaRequest | null>(
		null
	);

	const { editor, id } = useQuillEditor({
		value: typeof value === 'string' ? value : undefined,
		onChange(html) {
			onChange?.(html);
			onEditorChange?.(html, editor.current);
		},
		disabled,
		preset,
		uploadImage,
		onError,
		onFormulaRequest: setFormulaRequest,
	});

	const applyFormula = (latex: string) => {
		const quill = editor.current;
		if (quill && formulaRequest) {
			const { index, length } = formulaRequest;
			if (length > 0) quill.deleteText(index, length, 'user');
			if (latex) {
				quill.insertEmbed(index, 'formula', latex, 'user');
				quill.setSelection(index + 1, 0, 'silent');
			}
		}
		setFormulaRequest(null);
	};

	return (
		<Wrapper>
			<StyledEditor min_height={min_height ?? 200} max_height={max_height}>
				<div id={id} className={id}></div>
			</StyledEditor>
			{formulaRequest && (
				<FormulaModalBoundary
					onFail={() => {
						setFormulaRequest(null);
						onError?.(
							'The equation editor failed to open. Please reload the page and try again.'
						);
					}}
				>
					<Suspense fallback={null}>
						<MathModal
							initialLatex={formulaRequest.latex}
							onCancel={() => setFormulaRequest(null)}
							onInsert={applyFormula}
						/>
					</Suspense>
				</FormulaModalBoundary>
			)}
		</Wrapper>
	);
};

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const StyledEditor = styled.div<{ min_height?: number; max_height?: number }>`
	border: 2px solid #eee;
	border-radius: 10px;
	box-shadow: none;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;

	min-height: ${({ min_height }) => `${min_height ?? 200}px`};
	${({ max_height }) =>
		max_height
			? `max-height: ${max_height}px; overflow: auto;
	.ql-toolbar.ql-snow { position: sticky; top: 0; z-index: 2; }`
			: ''}

	.quill {
		display: flex;
		flex-direction: column;
	}
	.ql-tooltip {
		z-index: 999999;
	}
	.ql-toolbar {
		border-top-left-radius: 8px;
		border-top-right-radius: 8px;
	}
	.ql-container {
		flex: 1;
		border-bottom-left-radius: 0.25rem;
		border-bottom-right-radius: 0.25rem;
	}
	.ql-toolbar.ql-snow {
		border: unset;
		background-color: #fff;
		border-bottom: none;
		box-shadow: 0 2px 2px -2px rgba(34, 47, 62, 0.1),
			0 8px 8px -4px rgba(34, 47, 62, 0.07);
		padding: 4px 0;
	}
	.ql-container.ql-snow {
		border: unset;
	}
	.ql-snow .ql-picker.ql-header,
	.ql-snow .ql-picker.ql-font,
	.ql-snow .ql-picker.ql-size {
		&:hover {
			background: #f0f0f0;
			border: 0;
			box-shadow: none;
			color: #222f3e;
		}
		background: #f7f7f7;
		padding: 0 4px;
		user-select: none;
		width: 130px;
		align-items: center;
		background: #fff;
		border: 0;
		border-radius: 3px;
		box-shadow: none;
		color: #222f3e;
		display: flex;
		flex: 0 0 auto;
		font-size: 14px;
		font-style: normal;
		font-weight: 400;
		height: 28px;
		justify-content: center;
		margin: 6px 1px 5px 0;
		outline: 0;
		padding: 0;
		text-transform: none;
	}
	.ql-snow .ql-picker.ql-header.ql-expanded .ql-picker-label,
	.ql-snow .ql-picker.ql-font.ql-expanded .ql-picker-label,
	.ql-snow .ql-picker.ql-size.ql-expanded .ql-picker-label {
		background: #f0f0f0;
		border: 0;
		box-shadow: none;
		color: #222f3e;
	}
	/* Size registers as an inline-STYLE attributor, so its values are CSS
	   lengths; Quill's stylesheet only labels the small/large/huge keyword
	   values, so without these the picker would read '10px', '18px', '32px'. */
	.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before,
	.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before {
		content: 'Small';
	}
	.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before,
	.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before {
		content: 'Large';
	}
	.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before,
	.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before {
		content: 'Huge';
	}
	.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before { font-size: 10px; }
	.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before { font-size: 18px; }
	.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before { font-size: 32px; }
	.ql-toolbar.ql-snow .ql-picker-label {
		display: flex;
		align-items: center;
	}

	.ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-options {
		background-color: #fff;
		border: 1px solid transparent;
		border-radius: 6px;
		box-shadow: 0 0 2px 0 rgba(34, 47, 62, 0.2),
			0 4px 8px 0 rgba(34, 47, 62, 0.15);
		display: inline-block;
		overflow: hidden;
		vertical-align: top;
		z-index: 1150;
	}
	.ql-snow .ql-picker-options .ql-picker-item {
		background-color: #fff;
		color: #222f3e;
		padding: 4px 8px;
		align-items: center;
		border-radius: 3px;
		color: #222f3e;
		display: flex;
		-webkit-touch-callout: none;
		-webkit-user-select: none;
		user-select: none;
		&:hover {
			background-color: #006ce7;
			color: #fff;
		}
	}
	.ql-snow .ql-picker-options .ql-picker-item.ql-selected {
		background-color: #006ce7;
		color: #fff;
	}
	img.ql-image-uploading {
		opacity: 0.4;
		filter: grayscale(0.5);
	}
	img.ql-image-upload-failed {
		opacity: 0.5;
		outline: 2px dashed #e60000;
		outline-offset: 2px;
	}
`;

export default RichTextEditor;
